import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  QrCodeIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  BookOpenIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useFarm } from '../contexts/FarmContext';
import { supabase } from '../config/supabase';
import itraceSerializationService from '../services/itraceSerializationService';
import ITraceQRViewerModal from '../components/ITraceQRViewerModal';
import PageErrorBoundary from '../components/PageErrorBoundary';

// Completed Cycles are loaded 50 at a time ("Show more" loads the next 50).
const CYCLES_PAGE_SIZE = 50;

const ITraceabilityContent = () => {
  const { farms } = useFarm();
  const [completedCycles, setCompletedCycles] = useState([]);
  const [loadingCycles, setLoadingCycles] = useState(true);
  const [loadingMoreCycles, setLoadingMoreCycles] = useState(false);
  const [hasMoreCycles, setHasMoreCycles] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const [qrViewerCycle, setQrViewerCycle] = useState(null);
  const loadedFarmsKeyRef = useRef(null); // farm set the list was last loaded for
  const loadSeqRef = useRef(0);           // goes up on every reload from row 0

  // Loads one page of completed cycles: `offset` 0 replaces the list, a larger
  // offset appends the next page.
  const loadCompleted = useCallback(async (offset) => {
    // A reload from row 0 makes every reply still on its way out of date.
    if (offset === 0) loadSeqRef.current += 1;
    const seq = loadSeqRef.current;
    if (!farms?.length) {
      setCompletedCycles([]);
      setHasMoreCycles(false);
      setLoadingCycles(false);
      setLoadingMoreCycles(false);
      return;
    }
    if (offset === 0) setLoadingCycles(true);
    else setLoadingMoreCycles(true);
    try {
      const farmIds = farms.map(f => f.farm_id || f.id).filter(Boolean);

      // Only cycles whose harvest end date is today or earlier: the field is
      // also filled in as a plan, so future-dated cycles are not completed yet.
      // date_harvest_end is a DATE column, so compare with today's local date
      // (a timestamp string would be cut to a date and exclude today).
      const now = new Date();
      const todayLocal = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
      ].join('-');

      // For admins with thousands of farms a `.in('farm', [...])` URL exceeds
      // Supabase's URL length limit (HTTP 400). In that case we skip the farm
      // filter entirely — RLS still restricts what the user can read.
      // One row more than a page is requested to know whether more exist.
      let query = supabase
        .from('season')
        .select(`
          id, name_season, date_start, date_end, date_harvest_end,
          farm:farm(id, farmName),
          produce:produce_name(id, name_produce)
        `)
        .not('date_harvest_end', 'is', null)
        .lte('date_harvest_end', todayLocal)
        .order('date_harvest_end', { ascending: false })
        .order('id', { ascending: false })
        .range(offset, offset + CYCLES_PAGE_SIZE);

      const FARM_FILTER_MAX = 300;
      if (farmIds.length > 0 && farmIds.length <= FARM_FILTER_MAX) {
        query = query.in('farm', farmIds);
      }

      const { data, error: err } = await query;
      if (seq !== loadSeqRef.current) return; // reply for an earlier farm set
      if (err) {
        console.error('Error loading completed cycles:', err);
        if (offset === 0) {
          setCompletedCycles([]);
          setHasMoreCycles(false);
        }
      } else if (data) {
        const page = data.slice(0, CYCLES_PAGE_SIZE);
        setHasMoreCycles(data.length > CYCLES_PAGE_SIZE);
        setCompletedCycles((prev) => {
          if (offset === 0) return page;
          const seen = new Set(prev.map(c => c.id));
          return [...prev, ...page.filter(c => !seen.has(c.id))];
        });
      }
    } catch (e) {
      console.error('Error loading completed cycles:', e);
    } finally {
      if (seq === loadSeqRef.current) {
        setLoadingCycles(false);
        setLoadingMoreCycles(false);
      }
    }
  }, [farms]);

  // Reload from row 0 only when the set of farms really changes. FarmContext
  // re-fetches the farms (new array, same farms) each time the browser tab is
  // shown again or the session token refreshes; reloading then would throw
  // away the rows loaded with "Show more".
  useEffect(() => {
    const farmsKey = (farms || []).map(f => f.farm_id || f.id).sort().join(',');
    if (farmsKey === loadedFarmsKeyRef.current) return;
    loadedFarmsKeyRef.current = farmsKey;
    loadCompleted(0);
  }, [farms, loadCompleted]);

  const openPortal = async () => {
    setOpening(true);
    setError('');
    try {
      // The new tab is opened inside this click (before the first await) so
      // pop-up blockers allow it; it is pointed at the portal once the token arrives.
      await itraceSerializationService.openPortalInNewTab();
    } catch (e) {
      setError(`Could not open the iTrace portal. ${e.message || ''}`.trim());
    } finally {
      setOpening(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <QrCodeIcon className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
            <span>iTraceability</span>
          </h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Manage product traceability and QR code commissions via the iTrace Serialization portal.
          </p>
        </div>
        <Link
          to="/documentation"
          className="btn-secondary flex items-center gap-2 self-start sm:self-auto whitespace-nowrap"
          data-testid="docs-link"
        >
          <BookOpenIcon className="h-4 w-4" />
          <span>Documentation</span>
        </Link>
      </div>

      {/* How it works */}
      <div
        className="bg-blue-50 border border-blue-200 rounded-xl p-6"
        data-testid="itrace-how-it-works"
      >
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-blue-900">How QR codes are generated</h2>
            <p className="text-sm text-blue-800 mt-1">
              QR codes are generated inside the iTrace Serialization system, after a harvest
              has been sent to iTrace as a <em>Plant Batch</em>. The end-to-end flow is:
            </p>
            <ol className="list-decimal list-inside text-sm text-blue-900 mt-2 space-y-1">
              <li>Farm → Cycle → End Cycle (in <strong>Farm Management</strong>) — harvest is sent to iTrace as a Plant Batch</li>
              <li>iTrace Portal → <em>Commission Tasks → New Commission</em> → link the Plant Batch in <em>Batch Source</em></li>
              <li>iTrace generates the QR labels (trace codes)</li>
              <li>
                <strong>View and print them inside SIRIM:</strong> on any completed cycle row below,
                click <strong>"View QR Codes"</strong> to see every QR as a scannable image, copy a
                trace URL, download a single PNG, or print / save the whole commission as a PDF.
              </li>
            </ol>
          </div>
        </div>
      </div>

      {/* Open portal action */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Open iTrace Serialization Portal</h3>
            <p className="text-sm text-gray-600 mt-1">
              Launches the portal in a new tab with a one-time auto-login token. Use this to
              create commissions, print QR labels, or view existing ones.
            </p>
          </div>
          <button
            onClick={openPortal}
            disabled={opening}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
            data-testid="open-itrace-portal-btn"
          >
            {opening ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Opening...
              </>
            ) : (
              <>
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                Open Portal
              </>
            )}
          </button>
        </div>
        {error && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            {error}
          </div>
        )}
      </div>

      {/* Completed cycles — ones that can have Plant Batches / Commissions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Completed Cycles</h3>
          <span className="text-xs text-gray-500">Cycles whose harvest has been ended</span>
        </div>
        {loadingCycles ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : completedCycles.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircleIcon className="h-12 w-12 mx-auto text-gray-300" />
            <p className="mt-2 text-gray-500">No completed cycles yet.</p>
            <p className="text-sm text-gray-400">
              Complete a cycle in <Link to="/farm-management" className="text-blue-600 hover:underline">Farm Management</Link> to send a harvest to iTrace.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cycle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Farm</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produce</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Harvest Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {completedCycles.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{c.name_season}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{c.farm?.farmName || '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{c.produce?.name_produce || '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{formatDate(c.date_harvest_end || c.date_end)}</td>
                    <td className="px-6 py-3 text-right">
                      <div className="inline-flex items-center gap-3">
                        <button
                          onClick={() => setQrViewerCycle(c)}
                          className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-900"
                          title="View and print the QR codes generated by iTrace for this cycle"
                          data-testid={`view-qr-${c.id}`}
                        >
                          <EyeIcon className="h-4 w-4" />
                          View QR Codes
                        </button>
                        <button
                          onClick={openPortal}
                          disabled={opening}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                          Open in iTrace
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMoreCycles && (
              <div className="px-6 py-3 border-t border-gray-200 text-center">
                <button
                  onClick={() => loadCompleted(completedCycles.length)}
                  disabled={loadingMoreCycles}
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  data-testid="completed-cycles-show-more"
                >
                  {loadingMoreCycles ? 'Loading...' : 'Show more'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ITraceQRViewerModal
        isOpen={!!qrViewerCycle}
        onClose={() => setQrViewerCycle(null)}
        cycle={qrViewerCycle}
      />
    </div>
  );
};

// A render error anywhere on the iTraceability page shows a "Something went wrong"
// card with a Reload button instead of a blank screen (same as the HAVVA page).
const ITraceability = () => (
  <PageErrorBoundary pageName="iTraceability">
    <ITraceabilityContent />
  </PageErrorBoundary>
);

export default ITraceability;
